<script setup>
  import { Loader } from "lucide-vue-next";
  import { watch, onMounted, onUnmounted, getCurrentInstance } from "vue";
  import { storeToRefs } from "pinia";
  import { Card, CardContent } from "@/components/ui/card";
  import { Button } from "@/components/ui/button";
  import { Switch } from "@/components/ui/switch";
  import { ScrollArea } from "@/components/ui/scroll-area";
  import { useMMediaStore } from "@stores/mMedia";
  import { useMCreativeStore } from "@stores/mCreative";
  import { useMTemplatesStore } from "@stores/mTemplates";
  import CreativeLivePreview from "@/components/MCampaign/Creatives/CreativeLivePreview.vue";

  const instance = getCurrentInstance();
  const cable = instance.proxy.$cable;

  const getZenManUrl = () => {
    return new URL("@assets/images/zen_man.png", import.meta.url).href;
  };

  const mMediaStore = useMMediaStore();
  const {
    previewMMediumUrl,
    livePreviewTemplate,
    livePreviewVariables,
    previewPlacement,
    selectedTemplates,
    selectedAssets,
    selectedPlacements,
  } = storeToRefs(mMediaStore);
  const {
    selectMMedium,
    isMMediumSelected,
    getMMedium,
    getPreviewUrl,
    buildMMedia,
    isBuildRunning,
    adsChannelHandlers,
    updatePreviewPlacement,
  } = mMediaStore;

  const { fetchMTemplates } = useMTemplatesStore();

  const mCreativeStore = useMCreativeStore();
  const { mCreative } = storeToRefs(mCreativeStore);

  watch([() => selectedTemplates.value, () => selectedAssets.value], () => selectMMedium());

  watch(
    () => selectedPlacements.value,
    () => {
      if (selectedPlacements.value.length === 1) updatePreviewPlacement(selectedPlacements.value[0]);
    },
  );

  onMounted(() => {
    cable.subscribe({ channel: "AdsChannel" });
    cable._addChannel("AdsChannel", adsChannelHandlers(cable), {
      _uid: instance.uid,
      _value: instance.proxy,
    });
  });

  onUnmounted(() => {
    cable.unsubscribe("AdsChannel");
  });
</script>

<template>
  <div class="sticky h-[calc(100vh-100px)] flex flex-col gap-4">
    <Card>
      <CardContent class="p-4">
        <CreativeLivePreview
          v-if="livePreviewTemplate?.document_url"
          :document-url="livePreviewTemplate.document_url"
          :placement="previewPlacement"
          :variables="livePreviewVariables"
          :refresh-document-url="fetchMTemplates" />
        <div
          v-else
          class="flex w-full items-center justify-center bg-muted aspect-square">
          <img
            v-if="previewMMediumUrl && mCreative.media_type === 'image'"
            class="max-h-full object-scale-down"
            :src="previewMMediumUrl"
            alt="image-preview" />
          <video
            v-else-if="previewMMediumUrl && mCreative.media_type === 'video'"
            class="max-h-full object-scale-down"
            :src="previewMMediumUrl"
            alt="video-preview"
            autoplay
            loop
            muted />
          <img
            v-else
            class="max-h-full object-scale-down"
            :src="getZenManUrl()"
            alt="No preview yet" />
        </div>
      </CardContent>
    </Card>
    <Card class="flex-1 min-h-0 flex flex-col">
      <CardContent class="p-4 flex-1 min-h-0 flex flex-col">
        <div
          v-if="mCreative.media_type === 'image'"
          class="flex border-b border-border px-1 pb-1 mb-1 shrink-0">
          <div class="flex w-3/4 items-center justify-start">
            <label class="flex items-center gap-2">
              <span class="text-sm font-medium">Feed</span>
              <Switch
                :model-value="previewPlacement === 'story'"
                :disabled="selectedPlacements.length < 2"
                @update:model-value="updatePreviewPlacement($event ? 'story' : 'feed')" />
              <span class="text-sm font-medium">Story</span>
            </label>
          </div>
          <div class="w-1/4">
            <Button @click="buildMMedia()">Build</Button>
          </div>
        </div>
        <div class="overflow-hidden flex-1 min-h-0">
          <ScrollArea class="h-full p-1">
            <div
              v-for="(template, i) in selectedTemplates"
              :key="`selection-template-${i}`">
              <div class="py-1 text-sm font-medium">
                {{ template.name }}
              </div>
              <ul class="inline-flex flex-wrap gap-0 list-none p-0 m-0">
                <li
                  v-for="(asset, index) in selectedAssets"
                  :key="`creative-media-${index}`"
                  class="relative mr-2 mb-2 flex h-24 w-24 cursor-pointer items-center justify-center overflow-hidden rounded-md border-2 bg-muted"
                  :class="isMMediumSelected(asset.id, template.id) ? 'border-primary' : 'border-border'"
                  @click="selectMMedium(asset.id, template.id)">
                  <Loader
                    v-if="isBuildRunning(asset.id, template.id)"
                    :stroke-width="2.5"
                    class="size-7 absolute z-10 animate-spin text-primary" />
                  <img
                    v-if="mCreative.media_type === 'image'"
                    class="block h-full w-full max-w-full object-contain"
                    :class="{ 'opacity-50': !getMMedium(asset.id, template.id).url }"
                    :src="getMMedium(asset.id, template.id).url || getPreviewUrl(asset.id)"
                    alt="image_thumbnail" />
                  <video
                    v-else
                    class="block h-full w-full max-w-full object-contain"
                    :src="getMMedium(asset.id, template.id).url || getPreviewUrl(asset.id)"
                    alt="video-thumbnail" />
                </li>
              </ul>
            </div>
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  </div>
</template>
